import { Button, ConfigProvider, Flex, Modal, Typography } from "antd";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { translate } from "shared/languages";
import { theme } from "shared/theme";
import type { ReactNode } from "react";
import type { FormInstance } from "antd";
import { RequiredChoiceButton } from "components/modals/confirmModal";

/**
 * Standardized destructive confirm (prototype "Pembatalan Jadwal" pattern):
 * amber alert badge + single title, the message, and a footer with a
 * solid-ink Batal dismiss and a red destructive confirm — never a blue OK.
 * One shared modal for Hapus Kelas, Hapus Paket, and future destructive
 * actions.
 */
export const showDestructiveConfirm = ({
  title,
  message,
  extra,
  width = 400,
  confirmLabel,
  requireChoice,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  /** Rendered under the message, e.g. a choice the confirm depends on. The
   *  modal is static, so a control here should be uncontrolled and report
   *  its value through its own onChange, or live in a caller-owned Form
   *  (which is what requireChoice watches). */
  extra?: ReactNode;
  /** Wider than the default 400 when `extra` carries a real form. */
  width?: number;
  confirmLabel: string;
  /** Keeps the confirm disabled until this field of the caller's form has a
   *  value, the same mechanism showConfirmModal uses. */
  requireChoice?: {
    form: FormInstance;
    name: string;
    requiredWhen?: { choiceIs: unknown; field: string };
  };
  onConfirm: () => void;
  /** Runs on dismiss or close. Lets a caller that hid its own modal to avoid
   *  stacking put it back. */
  onCancel?: () => void;
}) => {
  const dismiss = () => {
    onCancel?.();
    Modal.destroyAll();
  };
  Modal.warning({
    icon: null,
    width,
    title: <ModalHeader tone="amber" title={title} />,
    content: (
      <>
        <Typography.Text style={modalSubtitleStyle}>{message}</Typography.Text>
        {/* A static modal renders outside the app's ConfigProvider, so a
            control in `extra` would otherwise fall back to AntD's default
            blue instead of the app's ink. */}
        {extra && <ConfigProvider theme={theme}>{extra}</ConfigProvider>}
      </>
    ),
    closable: true,
    maskClosable: true,
    footer: (
      <ConfigProvider theme={modalButtonsTheme}>
        <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
          <Button onClick={dismiss}>{translate("button.cancel")}</Button>
          {requireChoice ? (
            <RequiredChoiceButton
              choiceForm={requireChoice.form}
              choiceName={requireChoice.name}
              requiredWhen={requireChoice.requiredWhen}
              type="primary"
              danger
              onClick={() => {
                onConfirm();
                Modal.destroyAll();
              }}
            >
              {confirmLabel}
            </RequiredChoiceButton>
          ) : (
            <Button
              type="primary"
              danger
              onClick={() => {
                onConfirm();
                Modal.destroyAll();
              }}
            >
              {confirmLabel}
            </Button>
          )}
        </Flex>
      </ConfigProvider>
    ),
    onCancel: dismiss,
  });
};

/**
 * Same chrome as showDestructiveConfirm, but for a CAUTION rather than a
 * deletion: amber alert badge, solid-ink Batal, and a NAVY primary confirm
 * instead of a red destructive one.
 *
 * The distinction matters. A red button says "this removes something"; enabling
 * package sharing on an already-sold product does not remove anything, it
 * widens who may use it. Reusing the destructive helper would have made a
 * reversible settings change look like a delete.
 */
export const showCautionConfirm = ({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  /** Runs when the modal is dismissed OR closed, so a caller that optimistically
   *  flipped a control can put it back. */
  onCancel?: () => void;
}) => {
  const dismiss = () => {
    onCancel?.();
    Modal.destroyAll();
  };
  Modal.warning({
    icon: null,
    width: 400,
    title: <ModalHeader tone="amber" title={title} />,
    content: (
      <Typography.Text style={modalSubtitleStyle}>{message}</Typography.Text>
    ),
    closable: true,
    maskClosable: true,
    footer: (
      <ConfigProvider theme={modalButtonsTheme}>
        <Flex justify="flex-end" gap={10} style={{ marginTop: 22 }}>
          <Button onClick={dismiss}>{translate("button.cancel")}</Button>
          <Button
            type="primary"
            onClick={() => {
              onConfirm();
              Modal.destroyAll();
            }}
          >
            {confirmLabel}
          </Button>
        </Flex>
      </ConfigProvider>
    ),
    onCancel: dismiss,
  });
};
